import { ACTIONABLE_DENIAL_CODES, normalizeCas } from 'components/lab/billingEraReports/utils'
import { toNum } from 'components/lab/orderEdit/helpers'
import { formatCalendarDate } from 'helpers/dates'
import {
  BillingTransactionClaim,
  EraAutoPostReason,
  EraAutoPostState,
  EraAutoPostVerdictReason,
  EraClaimMatchMethod,
  EraClaimMatchStatus,
  EraClaimReviewState,
} from 'store/types'

// Methods that settle which charge a claim belongs to, and the fallback ones
// that can only ever propose it.
const CONFIRMED_MATCH_METHODS: EraClaimMatchMethod[] = ['CLAIM_NUMBER', 'MANUAL']
const FALLBACK_MATCH_METHODS: EraClaimMatchMethod[] = ['MEMBER_DOS_CPT', 'PATIENT_DOS_CPT']

/**
 * Where a claim stands on being matched, from its charge and how that charge
 * was arrived at. Mirrors the server's `deriveEraClaimMatchStatus`, which the
 * exception queue's filter is written against.
 */
export const getClaimMatchStatus = (claim: BillingTransactionClaim): EraClaimMatchStatus => {
  const isConfirmed = Boolean(
    claim.matchMethod && CONFIRMED_MATCH_METHODS.includes(claim.matchMethod),
  )
  const isFallback = Boolean(
    claim.matchMethod && FALLBACK_MATCH_METHODS.includes(claim.matchMethod),
  )

  if (claim.chargeId && isConfirmed) {
    return 'MATCHED'
  }

  if (claim.chargeId && isFallback) {
    return 'SUGGESTED'
  }

  // A fallback method with no charge to show for it means the matcher found
  // several equally good candidates and refused to pick one.
  return isFallback ? 'AMBIGUOUS' : 'UNMATCHED'
}

// How a claim's state is read.
//
// The server decides it and stamps it on the claim; the classifier below only
// covers claims that reach the browser without one. Always go through
// `getClaimReviewState`, which prefers the stamped verdict — that is the one
// the exception queue was filtered on, and a badge disagreeing with the queue
// a row arrived from is worse than a slightly stale badge.

const getStatusCode = (claim: BillingTransactionClaim): string | undefined =>
  claim.claimStatusCode || undefined

const collectClaimAdjustments = (claim: BillingTransactionClaim) => [
  ...(claim.serviceLines ?? []).flatMap((line) => normalizeCas(line?.serviceAdjustments ?? [])),
  ...normalizeCas(claim.claimAdjustments ?? []),
]

/** Mirrors the server's `classifyEraClaim`, decision for decision. */
const computeClaimReviewState = (claim: BillingTransactionClaim): EraClaimReviewState => {
  const statusCode = getStatusCode(claim)
  const paidAmount = toNum(claim.paidAmount)

  // Held under a Needs Work item, whatever the payer said.
  if (claim.parkedAt) {
    return 'PARKED'
  }

  if (statusCode === '22' || paidAmount < 0) {
    return 'REVERSAL'
  }

  if (claim.paymentId) {
    return 'POSTED'
  }

  if (claim.archived === true || !claim.claimNumber) {
    return 'NON_POSTABLE'
  }

  if (statusCode === '23') {
    return 'NOT_OUR_CLAIM'
  }

  if (statusCode === '25') {
    return 'PREDETERMINATION'
  }

  if (statusCode === '4') {
    return 'DENIED'
  }

  const adjustments = collectClaimAdjustments(claim)
  const patientResponsibility =
    claim.patientResponsibilityAmount !== undefined && claim.patientResponsibilityAmount !== null
      ? toNum(claim.patientResponsibilityAmount)
      : adjustments.filter((a) => a?.group === 'PR').reduce((acc, a) => acc + toNum(a?.amount), 0)

  if (paidAmount === 0) {
    // Processed but $0 paid: PR adjustments mean the balance moved to the
    // patient (deductible/coinsurance) — postable, not a denial. A full
    // payer-side (CO/OA/PI) adjustment with no PR is a zero-pay denial.
    if (patientResponsibility > 0) {
      return 'PATIENT_RESP'
    }

    if (adjustments.length > 0) {
      return 'DENIED'
    }

    return 'POSTABLE'
  }

  // Paid claims: flag partial denials (a $0 line fully adjusted to the payer
  // side) and actionable CARCs that need appeal/correction, not routine posting.
  const hasActionableCode = adjustments.some(
    (a) => a?.group !== 'PR' && ACTIONABLE_DENIAL_CODES.includes(a?.reason || ''),
  )
  const hasZeroPaidDeniedLine = (claim.serviceLines ?? []).some((line) => {
    if (toNum(line?.servicePaymentInformation?.lineItemProviderPaymentAmount) !== 0) return false
    const lineAdjustments = normalizeCas(line?.serviceAdjustments ?? [])
    return lineAdjustments.length > 0 && !lineAdjustments.some((a) => a?.group === 'PR')
  })

  if (hasActionableCode || hasZeroPaidDeniedLine) {
    return 'REVIEW'
  }

  return 'POSTABLE'
}

export const getClaimReviewState = (claim: BillingTransactionClaim): EraClaimReviewState =>
  claim.reviewState ?? computeClaimReviewState(claim)

export const getClaimStateColor = (state: EraClaimReviewState): string => {
  switch (state) {
    case 'REVERSAL':
      return '#9370FF'
    case 'DENIED':
      return '#FF3A5B'
    case 'PATIENT_RESP':
      return '#17A2B8'
    case 'REVIEW':
      return '#FEBF4C'
    case 'POSTED':
      return '#3CAEFF'
    case 'PARKED':
      return '#E8590C'
    case 'NOT_OUR_CLAIM':
    case 'PREDETERMINATION':
    case 'NON_POSTABLE':
      return '#C4C4C4'
    case 'POSTABLE':
    default:
      return '#29C788'
  }
}

export const getClaimStateLabel = (state: EraClaimReviewState): string => {
  switch (state) {
    case 'REVERSAL':
      return 'Reversal'
    case 'DENIED':
      return 'Denied'
    case 'PATIENT_RESP':
      return 'Patient Resp.'
    case 'REVIEW':
      return 'Review'
    case 'POSTED':
      return 'Posted'
    case 'PARKED':
      return 'Parked'
    case 'NOT_OUR_CLAIM':
      return 'Not Our Claim'
    case 'PREDETERMINATION':
      return 'Predetermination'
    case 'NON_POSTABLE':
      return 'Non-Postable'
    case 'POSTABLE':
    default:
      return 'Postable'
  }
}

export const REVIEW_STATE_OPTIONS: { key: EraClaimReviewState; value: string }[] = [
  'REVIEW',
  'DENIED',
  'REVERSAL',
  'PATIENT_RESP',
  'NOT_OUR_CLAIM',
  'PREDETERMINATION',
  'NON_POSTABLE',
  'POSTABLE',
].map((state) => ({
  key: state as EraClaimReviewState,
  value: getClaimStateLabel(state as EraClaimReviewState),
}))

export const getMatchStatusLabel = (status?: EraClaimMatchStatus | null): string => {
  switch (status) {
    case 'MATCHED':
      return 'Matched'
    case 'SUGGESTED':
      return 'Suggested'
    case 'AMBIGUOUS':
      return 'Ambiguous'
    case 'UNMATCHED':
    default:
      return 'Unmatched'
  }
}

export const getMatchStatusColor = (status?: EraClaimMatchStatus | null): string => {
  switch (status) {
    case 'MATCHED':
      return '#29C788'
    case 'SUGGESTED':
      return '#FEBF4C'
    case 'AMBIGUOUS':
      return '#FF3A5B'
    case 'UNMATCHED':
    default:
      return '#C4C4C4'
  }
}

export const MATCH_STATUS_OPTIONS: { key: EraClaimMatchStatus; value: string }[] = [
  'UNMATCHED',
  'SUGGESTED',
  'AMBIGUOUS',
  'MATCHED',
].map((status) => ({
  key: status as EraClaimMatchStatus,
  value: getMatchStatusLabel(status as EraClaimMatchStatus),
}))

/** Human-readable version of how the matcher arrived at its answer. */
export const getMatchMethodLabel = (claim: BillingTransactionClaim): string => {
  switch (claim.matchMethod) {
    case 'CLAIM_NUMBER':
      return 'Matched on claim control number'
    case 'MEMBER_DOS_CPT':
      return 'Suggested from member ID, date of service and CPT'
    case 'PATIENT_DOS_CPT':
      return 'Suggested from patient name, date of service and CPT'
    case 'MANUAL':
      return 'Charge selected by a biller'
    default:
      return 'No charge matched'
  }
}

/**
 * How a claim's auto-post verdict reads to a biller.
 *
 * The distinction the labels have to carry is hold versus exception: a held
 * claim is nobody's work — it posts itself once its check date arrives, or a
 * lab tech posts it when the envelope does. Calling that "blocked" would send
 * people looking for something to fix.
 */
export const getAutoPostStateLabel = (state?: EraAutoPostState | null): string => {
  switch (state) {
    case 'POST':
      return 'Will auto-post'
    case 'HOLD':
      return 'Waiting'
    case 'EXCEPTION':
      return 'Needs review'
    case 'POSTED':
      return 'Auto-posted'
    default:
      return 'Not evaluated'
  }
}

export const getAutoPostStateColor = (state?: EraAutoPostState | null): string => {
  switch (state) {
    case 'POST':
    case 'POSTED':
      return '#29C788'
    case 'HOLD':
      return '#FEBF4C'
    case 'EXCEPTION':
      return '#FF3A5B'
    default:
      return '#C4C4C4'
  }
}

export const getAutoPostReasonLabel = (reason: EraAutoPostReason): string => {
  switch (reason) {
    case 'NOT_FULLY_PARSED':
      return 'Imported before claim status was recorded'
    case 'NOT_CONFIRMED_MATCH':
      return 'No confirmed charge'
    case 'EXCEPTION_REVIEW_STATE':
      return 'Claim needs review'
    case 'OUT_OF_BALANCE':
      return 'Adjustments do not add up to the charge'
    case 'DISALLOWED_ADJUSTMENT_CODE':
      return 'Adjustment code outside the auto-post list'
    case 'CPT_NOT_ALLOWLISTED':
      return 'Procedure outside the auto-post list'
    case 'PAYER_ID_MISMATCH':
      return 'Paying payer is not the payer billed'
    case 'WRONG_WATERFALL_POSITION':
      return 'Payer is not the one currently responsible'
    case 'CROSSOVER_NO_SECONDARY':
      return 'Forwarded to a secondary payer not on file'
    case 'CROSSOVER_BEYOND_TERTIARY':
      return 'Forwarded by the tertiary payer — nowhere to go'
    case 'PLAN_BLOCKED':
      return 'Cannot be posted as it stands'
    case 'DUPLICATE_REMIT':
      return 'Second copy of a remittance already posted'
    case 'PAYMENT_REVERSED':
      return 'Its payment was reversed — post again or archive'
    case 'PAYMENT_DELETED':
      return 'Its payment was deleted — post again or archive'
    case 'BEFORE_CHECK_DATE':
      // BPR16: a check's issue date, or the day an ACH/EFT settles.
      return 'Payment date has not arrived'
    case 'PAPER_CHECK':
      return 'Paper check — post when it arrives'
    default:
      return reason
  }
}

// The facts the verdict recorded for one reason: which procedure, which code,
// which payer, how far off.
const getAutoPostReasonDetail = (code: EraAutoPostReason, reason: EraAutoPostVerdictReason): string | null => {
  switch (code) {
    case 'CPT_NOT_ALLOWLISTED':
      return reason.disallowedCpts?.length ? reason.disallowedCpts.join(', ') : null
    case 'DISALLOWED_ADJUSTMENT_CODE':
      return reason.disallowedCodes?.length ? reason.disallowedCodes.join(', ') : null
    case 'PAYER_ID_MISMATCH':
      return reason.eraPayerIdentifier ? `ERA payer ID ${reason.eraPayerIdentifier}` : null
    case 'OUT_OF_BALANCE':
      return reason.balanceDelta ? `off by $${Math.abs(reason.balanceDelta).toFixed(2)}` : null
    case 'CROSSOVER_NO_SECONDARY':
      return reason.crossoverCarrierName || reason.crossoverPayerId || null
    default:
      return null
  }
}

/** Every reason a claim is sitting still, one sentence each, with its facts. */
export const getAutoPostReasons = (reason?: EraAutoPostVerdictReason | null): string[] =>
  (reason?.reasons ?? []).map((code) => {
    const detail = reason ? getAutoPostReasonDetail(code, reason) : null
    return detail ? `${getAutoPostReasonLabel(code)}: ${detail}` : getAutoPostReasonLabel(code)
  })

export type AutoPostExplanation = {
  state: EraAutoPostState
  title: string
  color: string
  reasons: string[]
}

/**
 * Why a remittance on the ERA screens did not post itself, or null when there
 * is nothing to explain: never evaluated, about to post, auto-posted, or
 * already taken in hand by a person (posted, archived, parked, a denial).
 * A hold says when it posts on its own, so nobody goes looking for a fix.
 */
export const explainAutoPost = (
  claim: Pick<
    BillingTransactionClaim,
    'autoPostState' | 'autoPostReason' | 'autoPostRetryAfter' | 'paymentId' | 'archived' | 'parkedAt' | 'denialId'
  >,
): AutoPostExplanation | null => {
  const state = claim.autoPostState

  if ((state !== 'HOLD' && state !== 'EXCEPTION') || claim.paymentId || claim.archived || claim.parkedAt || claim.denialId) {
    return null
  }

  const retryAfter = claim.autoPostRetryAfter || claim.autoPostReason?.retryAfter
  const title =
    state === 'EXCEPTION'
      ? 'Not auto-posted: needs review'
      : retryAfter
        ? `Not auto-posted yet: posts automatically on ${formatCalendarDate(retryAfter)}`
        : 'Not auto-posted: waiting to be posted by hand'

  return { state, title, color: getAutoPostStateColor(state), reasons: getAutoPostReasons(claim.autoPostReason) }
}

export const AUTO_POST_STATE_OPTIONS: { key: EraAutoPostState; value: string }[] = [
  'EXCEPTION',
  'HOLD',
  'POST',
  'POSTED',
].map((state) => ({
  key: state as EraAutoPostState,
  value: getAutoPostStateLabel(state as EraAutoPostState),
}))

export const isClaimArchivable = (claim: BillingTransactionClaim): boolean =>
  !claim.paymentId && !claim.archived && !claim.parkedAt && !claim.denialId

// What the row's posting button opens the form for. A denied remittance is
// recorded as a denial by default rather than posted.
export const getPostingActionLabel = (state: EraClaimReviewState): string => {
  switch (state) {
    case 'REVERSAL':
      return 'Post Reversal'
    case 'DENIED':
      return 'Record Denial'
    default:
      return 'Apply Payment'
  }
}
